import { useSupabaseUser } from '#imports';
import { onMounted, ref } from 'vue';
import type { PremiumStateResponse } from '~/types/premium';

export const usePremiumStatus = () => {
  const isPremium = ref(false);
  const exportCredits = ref(0);
  /** 'subscription' | 'pack' | null */
  const premiumSource = ref<PremiumStateResponse['source']>(null);
  /** ISO date when pack access ends (null for subscriptions). */
  const premiumUntil = ref<string | null>(null);
  /** Until when Premium month by month after a pack is offered (PACK_CONTINUATION), or null. */
  const continuationUntil = ref<string | null>(null);
  const isLoading = ref(false);
  /**
   * True once the plan is known — the check answered, or there was no session to
   * ask about. `isPremium` reads false until then, which is the right default
   * for a gate but the wrong one for an offer: anything that should be hidden
   * from a paying visitor waits for this instead, so a price never flashes past
   * somebody who has already paid.
   */
  const isChecked = ref(false);
  const error = ref('');
  const user = useSupabaseUser();

  const checkPremiumStatus = async () => {
    if (!user.value) {
      isPremium.value = false;
      exportCredits.value = 0;
      premiumSource.value = null;
      premiumUntil.value = null;
      continuationUntil.value = null;
      isChecked.value = true;
      return;
    }

    try {
      isLoading.value = true;
      error.value = '';

      const data = await $fetch<PremiumStateResponse>('/api/lemonsqueezy/check-subscription');

      isPremium.value = data.isPremium;
      exportCredits.value = data.exportCredits ?? 0;
      premiumSource.value = data.source ?? null;
      premiumUntil.value = data.until ?? null;
      continuationUntil.value = data.continuationUntil ?? null;
    } catch (e) {
      console.error('Error checking subscription status:', e);
      error.value = useNuxtApp().$i18n.t('premium.checkError');
    } finally {
      isLoading.value = false;
      isChecked.value = true;
    }
  };

  onMounted(() => {
    checkPremiumStatus();
  });

  return {
    isPremium,
    exportCredits,
    premiumSource,
    premiumUntil,
    continuationUntil,
    isLoading,
    isChecked,
    error,
    checkPremiumStatus,
  };
};
